import { toQueryFiltersString } from '@benzinga/scanner-manager/filter-utils';
import { WidgetLinkingManager, isTickerSelectedEvent } from '@benzinga/widget-linking';
import { NewsfeedFieldType } from './entities';
import { ScannerConfigManager } from '@benzinga/scanner-config-manager/manager';
import { HoldingsManager } from '@benzinga/data-manager-holdings';
import { VerticalsManager } from '@benzinga/verticals-manager';
import { GptSearchManager } from '@benzinga/benzinga-gpt-search-manager';
import { Session, StockSymbol, Token } from '@benzinga/session';
import { WatchlistManager } from '@benzinga/watchlist-manager';

const setupStockSymbol = <T extends StockSymbol | unknown>(symbol: T): T => {
  if (typeof symbol === 'string' && symbol.includes('/USD')) {
    // ForEx symbol's don't have base currency in the newsfeed backend but use a $ as a prefix
    // hence BTC/USD becomes $BTC
    const newSymbols = symbol.split('/');
    return `$${newSymbols[0]}` as T;
  } else {
    return symbol;
  }
};

export const expressionConvert =
  (session: Session) =>
  (
    token: Token<
      NewsfeedFieldType,
      | { symbol_link_id: string }
      | { watchlist_id: string }
      | { scanner_id: string }
      | { screener_id: string }
      | { screener_query: string }
      | { holding_name: string }
      | { vertical_name: string }
      | { prompt: string }
      | StockSymbol[]
    >,
  ): Token<NewsfeedFieldType, StockSymbol[] | { screener_query: string }> => {
    const value = token[2];
    if (
      (token[1] === 'all' || token[1] === 'any' || token[1] === 'in') &&
      typeof value === 'object' &&
      !Array.isArray(value)
    ) {
      return [
        token[0],
        token[1],
        Object.entries(value).reduce<StockSymbol[] | { screener_query: string }>((acc, [k, v]) => {
          if (Array.isArray(acc)) {
            switch (k) {
              case 'watchlist_id':
                return (
                  session
                    .getManager(WatchlistManager)
                    .getStoredWatchlistById(v)
                    ?.symbols.map(s => s.symbol) ?? acc
                );
              case 'screener_id':
                return {
                  screener_query: toQueryFiltersString(
                    session
                      .getManager(ScannerConfigManager)
                      .getConfigs()
                      ?.find(s => s.uuid === v)?.filters ?? [],
                  ),
                };
              case 'scanner_id':
                return {
                  screener_query: toQueryFiltersString(
                    session
                      .getManager(ScannerConfigManager)
                      .getConfigs()
                      ?.find(s => s.uuid === v)?.filters ?? [],
                  ),
                };
              case 'symbol_link_id': {
                const symbol = session
                  .getManager(WidgetLinkingManager)
                  .getWidgetLinkFeedByID(v)
                  ?.getLink()
                  .events.find(isTickerSelectedEvent)?.symbol;
                return symbol ? [symbol] : [];
              }
              case 'holding_name':
                return session.getManager(HoldingsManager).getCachedHoldingByName(v)?.holdingSymbols ?? [];
              case 'vertical_name':
                return session.getManager(VerticalsManager).getCachedVerticalByVerticalName(v)?.verticalSymbols ?? [];
              case 'screener_query':
                return { [k]: v };
              case 'prompt': {
                const prompt = session.getManager(GptSearchManager).getCachedWithPromptData(v)?.prompt;
                return prompt ? [prompt] : [];
              }
            }
          }
          return acc;
        }, []),
      ];
    } else if (
      (token[1] === 'all' || token[1] === 'any' || token[1] === 'in') &&
      token[0] === 'Tickers.name' &&
      Array.isArray(token[2])
    ) {
      return [token[0], token[1], token[2].map(v => setupStockSymbol(v))];
    }
    return token as Token<NewsfeedFieldType, StockSymbol[] | { screener_query: string }>;
  };
